import { useEffect, useRef } from "react";
import { usePlatformBrand } from "@/hooks/usePlatformBranding";
import { useTenantBranding } from "@/hooks/useTenantBranding";
import { useUIStore } from "@/store/useUIStore";

/**
 * Keeps the painted palette and the tab title in step with the workspace's branding. Renders
 * nothing.
 *
 * Mounted inside the ACCOUNT shell only: branding belongs to a workspace, and the platform
 * admin area has none, so it never runs there. Waits for data before writing, so a slow request
 * leaves the persisted palette on screen rather than flashing ORAI's colours in between.
 */
export function TenantBrandingSync() {
  const { data } = useTenantBranding();
  const setTenantPalette = useUIStore((s) => s.setTenantPalette);
  const platformName = usePlatformBrand().brand_name;
  // Read by the unmount cleanup, which must not re-run (and repaint) when the name changes.
  const platformNameRef = useRef(platformName);
  platformNameRef.current = platformName;

  useEffect(() => {
    if (data) setTenantPalette(data.palette);
  }, [data, setTenantPalette]);

  useEffect(() => {
    document.title = data?.custom_brand_name ?? platformName;
  }, [data, platformName]);

  // Leaving the workspace -- signing out, switching account, opening the platform admin area --
  // stops painting its brand. The palette is persisted, so without this the NEXT person to sign
  // in on this browser would see the previous tenant's colours on the login screen.
  useEffect(
    () => () => {
      setTenantPalette(null);
      document.title = platformNameRef.current;
    },
    [setTenantPalette],
  );

  return null;
}
